/**
 * FamilyMessages.js - Family messaging system
 *
 * COPPA compliant:
 * - Messages are family-only (not public)
 * - Parents can review/delete all messages
 * - No third-party sharing
 *
 * Available during screen lock:
 * - Child can always access family messages
 * - "Message Parents" button on ChildDashboard opens this page
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Badge,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Chat,
  Lock,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyId } from '../contexts/FamilyIdContext';
import { useFamily } from '../contexts/FamilyContext';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import MessageThread from '../components/MessageThread';

function FamilyMessages() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  const { familyId } = useFamilyId();
  const { familyMembers } = useFamily();

  const [selectedUser, setSelectedUser] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({}); // userId -> unread count
  const [loading, setLoading] = useState(true);

  const myId = currentUser?.uid;
  const myRole = userProfile?.role || 'child';

  // Filter to show: if parent → show children + other parents; if child → show parents only
  // const childContacts = familyMembers.filter(m => m.id !== myId && m.role === 'child');
  const parentContacts = familyMembers.filter(m => m.id !== myId && m.role === 'parent');
  
  // Main contact list (who the user messages)
  const contactList = familyMembers.filter(m =>
    m.id !== myId && (
      myRole === 'parent' ? m.role === 'child' : m.role === 'parent'
    )
  );
  
  // Parent-to-parent contacts (only visible to parents, hidden from children)
  const parentOnlyContacts = myRole === 'parent' ? parentContacts : [];

  // Auto-select user from query param (e.g., /messages?to=parentId)
  useEffect(() => {
    const toParam = searchParams.get('to');
    if (toParam && familyMembers.length > 0) {
      const target = familyMembers.find(m => m.id === toParam);
      if (target) setSelectedUser(target);
    } else if (contactList.length > 0 && !selectedUser) {
      // Auto-select first contact
      setSelectedUser(contactList[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyMembers, searchParams]);

  // Track unread message counts
  useEffect(() => {
    if (!familyId || !myId) {
      setLoading(false);
      return;
    }

    const messagesRef = collection(db, `families/${familyId}/messages`);
    const q = query(
      messagesRef,
      where('toId', '==', myId),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const counts = {};
      snap.docs.forEach(d => {
        const fromId = d.data().fromId;
        counts[fromId] = (counts[fromId] || 0) + 1;
      });
      setUnreadCounts(counts);
      setLoading(false);
    }, (err) => {
      console.error('[FamilyMessages] Unread listener error:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [familyId, myId]);

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate(myRole === 'parent' ? '/parent' : '/child')} edge="start">
            <ArrowBack />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              💬 Family Messages
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Family-only • Always available
            </Typography>
          </Box>
          {totalUnread > 0 && (
            <Badge badgeContent={totalUnread} color="error">
              <Chat sx={{ color: 'white' }} />
            </Badge>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Contacts sidebar - hidden on mobile when a chat is selected */}
        <Box sx={{ width: { xs: selectedUser ? 0 : '100%', sm: 200 }, borderRight: { sm: '1px solid #e0e0e0' }, overflow: 'auto', flexShrink: 0, display: { xs: selectedUser ? 'none' : 'flex', sm: 'block' }, flexDirection: 'column' }}>
          <Typography variant="subtitle2" sx={{ p: 1.5, fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>
            {myRole === 'parent' ? 'Children' : 'My Parents'}
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          ) : contactList.length === 0 && parentOnlyContacts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', fontSize: '0.8rem' }}>
              No family members yet
            </Typography>
          ) : (
            <>
              {contactList.map(contact => (
                <ListItem
                  key={contact.id}
                  button
                  selected={selectedUser?.id === contact.id}
                  onClick={() => setSelectedUser(contact)}
                  sx={{
                    borderRadius: 1,
                    mx: 0.5,
                    cursor: 'pointer',
                    '&.Mui-selected': { bgcolor: 'primary.light', color: 'primary.contrastText' },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Badge
                      badgeContent={unreadCounts[contact.id] || 0}
                      color="error"
                      overlap="circular"
                    >
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.85rem' }}>
                        {contact.displayName?.charAt(0)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={unreadCounts[contact.id] ? 'bold' : 'normal'} noWrap>
                        {contact.displayName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {contact.role}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}

              {/* Parents Only section - hidden from children */}
              {parentOnlyContacts.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ px: 1.5, py: 0.5, fontWeight: 'bold', color: 'warning.dark', fontSize: '0.7rem', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Lock sx={{ fontSize: 12 }} /> Parents Only
                  </Typography>
                  {parentOnlyContacts.map(contact => (
                    <ListItem
                      key={contact.id}
                      button
                      selected={selectedUser?.id === contact.id}
                      onClick={() => setSelectedUser(contact)}
                      sx={{
                        borderRadius: 1,
                        mx: 0.5,
                        cursor: 'pointer',
                        bgcolor: selectedUser?.id === contact.id ? 'warning.light' : 'rgba(255, 193, 7, 0.08)',
                        '&:hover': { bgcolor: 'warning.light' },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Badge
                          badgeContent={unreadCounts[contact.id] || 0}
                          color="error"
                          overlap="circular"
                        >
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.85rem', bgcolor: 'warning.main' }}>
                            {contact.displayName?.charAt(0)}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={unreadCounts[contact.id] ? 'bold' : 'normal'} noWrap>
                            {contact.displayName}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="warning.dark" noWrap>
                            👨‍👩 Parent
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </>
              )}
            </>
          )}
        </Box>

        {/* Message Thread */}
        <Box sx={{ flex: 1, flexDirection: 'column', overflow: 'hidden', display: { xs: selectedUser ? 'flex' : 'none', sm: 'flex' } }}>
          {selectedUser ? (
            <>
              {/* Thread header */}
              <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Back button - mobile only */}
                <IconButton size="small" sx={{ display: { xs: 'flex', sm: 'none' }, mr: 0.5 }} onClick={() => setSelectedUser(null)}>
                  <ArrowBack fontSize="small" />
                </IconButton>
                <Avatar sx={{ width: 36, height: 36 }}>{selectedUser.displayName?.charAt(0)}</Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">{selectedUser.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">{selectedUser.role}</Typography>
                </Box>
              </Box>

              {/* Conversation */}
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <MessageThread
                  familyId={familyId}
                  otherUserId={selectedUser.id}
                  otherUserName={selectedUser.displayName}
                  isParentsOnly={myRole === 'parent' && selectedUser.role === 'parent'}
                />
              </Box>
            </>
          ) : (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1} p={3} textAlign="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Chat sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">Select a family member to chat</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Messages are private to your family only
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* COPPA notice at bottom */}
      <Box sx={{ p: 1, bgcolor: 'grey.50', borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          🔒 Messages are private to your family. Parents can review all messages.
        </Typography>
      </Box>
    </Box>
  );
}

export default FamilyMessages;
